/**
 * HeaderSearchField — always-visible compact search input for a column
 * header (the Onboardings-list pattern: label + small TextField inline).
 *
 * Counterpart to `ExpandingHeaderSearch` (icon-that-expands): use THIS one
 * when search is a primary affordance of the list and should never hide;
 * use the expanding variant when the header is space-constrained.
 *
 * Wire it inside a column's `Header` render prop to MRT's filter engine
 * (`column.setFilterValue`) with `filterFn: 'contains'` on the column and
 * `enableColumnFilters: true` + `columnFilterDisplayMode: 'custom'` on the
 * table.
 *
 * Interaction guards (both load-bearing inside an MRT header cell):
 * - `onClick` stopPropagation — a click must focus the input, not trigger
 *   the header's sort/select-all handlers.
 * - `onKeyDown` stopPropagation — MRT's cell keyboard shortcuts
 *   (`enableKeyboardShortcuts`, default ON) listen on the th and hijack
 *   Arrow/Home/End to move cell focus; typing must not leak out.
 */

import React from 'react';
import { Box, InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

export interface HeaderSearchFieldProps {
  /** Column label rendered before the input (e.g. `'Name'`). */
  label: React.ReactNode;
  /** Controlled query value. */
  value: string;
  /** Change handler — typically `column.setFilterValue` glue. */
  onChange: (next: string) => void;
  /** Placeholder. Default `'Search...'`. */
  placeholder?: string;
  /** `data-testid` for the TextField ROOT (resolve the input via `.locator('input')`). */
  testId?: string;
}

export function HeaderSearchField({
  label,
  value,
  onChange,
  placeholder = 'Search...',
  testId,
}: HeaderSearchFieldProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {label}
      <TextField
        size="small"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Escape') onChange('');
        }}
        data-testid={testId}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16 }} color="action" />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          ml: 2,
          maxWidth: 200,
          '& .MuiOutlinedInput-root': { height: 28, fontSize: '0.8rem' },
          '& .MuiOutlinedInput-input': { py: 0.25, px: 0.5 },
        }}
      />
    </Box>
  );
}

export default HeaderSearchField;
